import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Modal from './Modal';
import Avatar from './Avatar';
import { removePhoto, savePhoto, usePhotos } from '../lib/photos';
import './PhotoEditor.css';

/**
 * "YOUR PHOTO" - choosing, taking, cropping and removing your own picture.
 *
 * Gordon: "Allow users to upload a picture of themselves so that the round
 * circles in the chat can be exchanged into a face pic."
 *
 * EVERYTHING HAPPENS ON THIS PAGE until Save. The picture - chosen, dropped
 * or taken with the camera - is drawn onto a canvas, positioned and zoomed
 * inside a circle, and only what is inside the square around that circle is
 * sent: once at 512 x 512 and once at 96 x 96, as WebP (JPEG where the
 * browser cannot write WebP). Drawing and encoding again is what removes a
 * camera's EXIF block, where phones keep the place a photo was taken - and
 * the page says so, because "location data removed" is the question somebody
 * uploading a selfie from their phone should not have to ask. The server
 * checks the result again regardless (lib/profile-photos.js).
 *
 * The crop works with a mouse (drag, wheel), a finger (drag, pinch) and a
 * keyboard (arrows nudge, Shift for more, + and - zoom, 0 resets), and the
 * three small circles beside it show exactly what colleagues will see in a
 * chat list, a message and a to-do.
 */

const OUT_FULL = 512;
const OUT_THUMB = 96;
const MAX_SOURCE = 2048;                     // the longest side we keep in memory
const MAX_FILE_BYTES = 30 * 1024 * 1024;
const MIN_ZOOM = 1;
const MAX_ZOOM = 4;

const clamp = (n, lo, hi) => Math.min(hi, Math.max(lo, n));

/** A canvas of the picture, no larger than MAX_SOURCE on its longest side. */
function toWorkingCanvas(img, w, h) {
  const k = Math.min(1, MAX_SOURCE / Math.max(w, h));
  const c = document.createElement('canvas');
  c.width = Math.max(1, Math.round(w * k));
  c.height = Math.max(1, Math.round(h * k));
  const ctx = c.getContext('2d');
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(img, 0, 0, c.width, c.height);
  return c;
}

/** Open a file as a picture, the right way up. */
async function decode(file) {
  if (file.size > MAX_FILE_BYTES) throw new Error('That picture is too large to work with (over 30 MB).');
  if (file.type && !file.type.startsWith('image/')) throw new Error('That file is not a picture. Choose a JPEG, PNG or WebP.');
  try {
    const bmp = await createImageBitmap(file, { imageOrientation: 'from-image' });
    const c = toWorkingCanvas(bmp, bmp.width, bmp.height);
    if (bmp.close) bmp.close();
    return c;
  } catch {
    // Older Safari, or a format createImageBitmap will not take: an <img>
    // decodes what the browser can show, the right way up.
    const url = URL.createObjectURL(file);
    try {
      const img = new Image();
      img.src = url;
      await img.decode();
      return toWorkingCanvas(img, img.naturalWidth, img.naturalHeight);
    } catch {
      throw new Error('That picture could not be opened. Try a JPEG, PNG or WebP.');
    } finally {
      URL.revokeObjectURL(url);
    }
  }
}

const blobOf = (canvas, type, quality) => new Promise((resolve) => canvas.toBlob(resolve, type, quality));

/** WebP where the browser can write it, otherwise JPEG; never anything else. */
async function encode(canvas, prefer) {
  if (prefer !== 'image/jpeg') {
    const webp = await blobOf(canvas, 'image/webp', 0.9);
    if (webp && webp.type === 'image/webp') return webp;
  }
  const jpeg = await blobOf(canvas, 'image/jpeg', 0.9);
  if (jpeg && jpeg.type === 'image/jpeg') return jpeg;
  throw new Error('This browser could not save the picture.');
}

/** Draw the chosen square of `source` into a new side x side canvas. */
function cropTo(source, view, side) {
  const c = document.createElement('canvas');
  c.width = side;
  c.height = side;
  const ctx = c.getContext('2d');
  ctx.imageSmoothingQuality = 'high';
  // A white page first: a PNG with transparency would otherwise come out
  // black in a JPEG, and a circle with a hole in it in WebP.
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, side, side);
  const s = view.side;
  ctx.drawImage(source, view.cx - s / 2, view.cy - s / 2, s, s, 0, 0, side, side);
  return c;
}

/** Halve until close, then the last step - sharper than one big jump. */
function downscale(canvas, side) {
  let cur = canvas;
  while (cur.width / 2 >= side * 1.5) {
    const half = document.createElement('canvas');
    half.width = Math.round(cur.width / 2);
    half.height = Math.round(cur.height / 2);
    const ctx = half.getContext('2d');
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(cur, 0, 0, half.width, half.height);
    cur = half;
  }
  const out = document.createElement('canvas');
  out.width = side;
  out.height = side;
  const ctx = out.getContext('2d');
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(cur, 0, 0, side, side);
  return out;
}

const coarse = () => {
  try { return window.matchMedia('(pointer: coarse)').matches; } catch { return false; }
};
const hasCamera = () => Boolean(typeof navigator !== 'undefined' && navigator.mediaDevices?.getUserMedia);

/* ================================================================ crop */

function Cropper({ source, zoom, setZoom, center, setCenter, labelId }) {
  const stageRef = useRef(null);
  const canvasRef = useRef(null);
  const previews = useRef([]);
  const pointers = useRef(new Map());
  const pinch = useRef(null);
  const [stage, setStage] = useState(288);

  const w = source.width;
  const h = source.height;
  const base = Math.min(w, h);
  const side = base / zoom;

  // Keep the circle inside the picture.
  const fit = useCallback((c, z) => {
    const s = base / z;
    return { x: clamp(c.x, s / 2, w - s / 2), y: clamp(c.y, s / 2, h - s / 2) };
  }, [base, w, h]);

  useEffect(() => {
    const el = stageRef.current;
    if (!el) return undefined;
    const measure = () => setStage(el.clientWidth || 288);
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Draw the stage and the three previews whenever anything moves.
  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const dpr = window.devicePixelRatio || 1;
    c.width = Math.round(stage * dpr);
    c.height = Math.round(stage * dpr);
    const ctx = c.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.imageSmoothingQuality = 'high';
    ctx.clearRect(0, 0, stage, stage);
    const k = stage / side;
    ctx.drawImage(source, stage / 2 - center.x * k, stage / 2 - center.y * k, w * k, h * k);
    for (const p of previews.current) {
      if (!p) continue;
      const px = Math.round(Number(p.dataset.size) * dpr);
      p.width = px;
      p.height = px;
      const pc = p.getContext('2d');
      pc.imageSmoothingQuality = 'high';
      pc.drawImage(source, center.x - side / 2, center.y - side / 2, side, side, 0, 0, px, px);
    }
  }, [source, center, side, stage, w, h]);

  const zoomTo = useCallback((z) => {
    const next = clamp(z, MIN_ZOOM, MAX_ZOOM);
    setZoom(next);
    setCenter((c) => fit(c, next));
  }, [fit, setCenter, setZoom]);

  const moveBy = useCallback((dx, dy) => {
    setCenter((c) => fit({ x: c.x + dx, y: c.y + dy }, zoom));
  }, [fit, setCenter, zoom]);

  // The wheel has to be a non-passive listener to stop the page scrolling.
  useEffect(() => {
    const el = stageRef.current;
    if (!el) return undefined;
    const onWheel = (e) => {
      e.preventDefault();
      zoomTo(zoom * Math.exp(-e.deltaY * 0.0015));
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [zoom, zoomTo]);

  const onPointerDown = (e) => {
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* not every pointer can be captured */ }
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.current.size === 2) {
      const [a, b] = [...pointers.current.values()];
      pinch.current = { dist: Math.hypot(a.x - b.x, a.y - b.y), zoom };
    }
  };
  const onPointerMove = (e) => {
    const prev = pointers.current.get(e.pointerId);
    if (!prev) return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.current.size >= 2 && pinch.current) {
      const [a, b] = [...pointers.current.values()];
      const dist = Math.hypot(a.x - b.x, a.y - b.y);
      if (pinch.current.dist > 0) zoomTo(pinch.current.zoom * (dist / pinch.current.dist));
      return;
    }
    const k = side / stage;            // image pixels per stage pixel
    moveBy(-(e.clientX - prev.x) * k, -(e.clientY - prev.y) * k);
  };
  const onPointerUp = (e) => {
    pointers.current.delete(e.pointerId);
    if (pointers.current.size < 2) pinch.current = null;
  };

  const onKeyDown = (e) => {
    const step = side * (e.shiftKey ? 0.1 : 0.02);
    const moves = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step] };
    if (moves[e.key]) {
      e.preventDefault();
      // The arrow says which way the PICTURE goes, as dragging does.
      moveBy(-moves[e.key][0], -moves[e.key][1]);
    } else if (e.key === '+' || e.key === '=') {
      e.preventDefault(); zoomTo(zoom + 0.1);
    } else if (e.key === '-' || e.key === '_') {
      e.preventDefault(); zoomTo(zoom - 0.1);
    } else if (e.key === '0') {
      e.preventDefault(); setZoom(1); setCenter({ x: w / 2, y: h / 2 });
    }
  };

  return (
    <div className="pe-crop">
      <div className="pe-crop-main">
        <div
          ref={stageRef}
          className="pe-stage"
          tabIndex={0}
          role="group"
          aria-labelledby={labelId}
          aria-describedby="pe-crop-help"
          aria-roledescription="photo position"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          onKeyDown={onKeyDown}
        >
          <canvas ref={canvasRef} className="pe-canvas" style={{ width: stage, height: stage }} aria-hidden="true" />
          <span className="pe-mask" aria-hidden="true" />
        </div>
        <div className="pe-zoom">
          <button type="button" className="pe-zoom-btn" aria-label="Zoom out"
                  onClick={() => zoomTo(zoom - 0.25)} disabled={zoom <= MIN_ZOOM}>−</button>
          <input type="range" className="pe-zoom-range" min={MIN_ZOOM} max={MAX_ZOOM} step={0.01}
                 value={zoom} aria-label="Zoom" aria-valuetext={`${Math.round(zoom * 100)}%`}
                 onChange={(e) => zoomTo(Number(e.target.value))} />
          <button type="button" className="pe-zoom-btn" aria-label="Zoom in"
                  onClick={() => zoomTo(zoom + 0.25)} disabled={zoom >= MAX_ZOOM}>+</button>
        </div>
        <p className="pe-help" id="pe-crop-help">
          Drag to move it, scroll or pinch to zoom. With the keyboard: arrow
          keys move it, + and − zoom, 0 starts again.
        </p>
      </div>
      <div className="pe-previews" aria-hidden="true">
        <span className="pe-previews-title">How it will look</span>
        <div className="pe-preview-row">
          {[64, 32, 24].map((size, i) => (
            <canvas key={size} ref={(el) => { previews.current[i] = el; }} data-size={size}
                    className="pe-preview" style={{ width: size, height: size }} />
          ))}
        </div>
      </div>
    </div>
  );
}

/* ============================================================ camera */

function Camera({ onTaken, onCancel, onError }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [live, setLive] = useState(false);

  useEffect(() => {
    let gone = false;
    navigator.mediaDevices.getUserMedia({
      video: { facingMode: 'user', width: { ideal: 1280 }, height: { ideal: 720 } }, audio: false
    }).then((stream) => {
      if (gone) { stream.getTracks().forEach((t) => t.stop()); return; }
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play().catch(() => {});
      }
    }).catch((e) => {
      if (gone) return;
      onError(e?.name === 'NotAllowedError'
        ? 'Jarvis was not allowed to use your camera. Allow it from the address bar, or choose a photo instead.'
        : 'No camera could be started. Choose a photo instead.');
    });
    return () => {
      gone = true;
      if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    };
  }, [onError]);

  const take = () => {
    const v = videoRef.current;
    if (!v || !v.videoWidth) return;
    // The picture as the camera saw it - not mirrored, so writing on a
    // T-shirt reads the right way round to everybody else.
    onTaken(toWorkingCanvas(v, v.videoWidth, v.videoHeight));
  };

  return (
    <div className="pe-camera">
      <div className="pe-stage pe-stage-camera">
        <video ref={videoRef} className="pe-video" playsInline muted autoPlay
               onLoadedMetadata={() => setLive(true)} aria-label="Your camera" />
        <span className="pe-mask" aria-hidden="true" />
        {!live && <span className="pe-camera-wait">Starting the camera…</span>}
      </div>
      <div className="pe-row">
        <button type="button" className="btn btn-secondary" onClick={onCancel}>Back</button>
        <button type="button" className="btn btn-primary" onClick={take} disabled={!live}>Take photo</button>
      </div>
    </div>
  );
}

/* ============================================================ the dialog */

export default function PhotoEditor({ name, onClose }) {
  const photos = usePhotos();
  const me = photos.me;
  const [step, setStep] = useState('view');      // view | camera | crop
  const [source, setSource] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [center, setCenter] = useState({ x: 0, y: 0 });
  const [busy, setBusy] = useState(false);
  const [opening, setOpening] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [dragging, setDragging] = useState(false);
  const fileRef = useRef(null);
  const selfieRef = useRef(null);

  const mayUpload = Boolean(me?.may_upload);
  const hasOwn = Boolean(me?.version);

  const begin = useCallback((canvas) => {
    setSource(canvas);
    setZoom(1);
    setCenter({ x: canvas.width / 2, y: canvas.height / 2 });
    setError(null);
    setNotice(null);
    setStep('crop');
  }, []);

  const openFile = useCallback(async (file) => {
    if (!file) return;
    setError(null);
    setOpening(true);
    try {
      begin(await decode(file));
    } catch (e) {
      setError(e.message || 'That picture could not be opened.');
    } finally {
      setOpening(false);
    }
  }, [begin]);

  const cameraFailed = useCallback((message) => { setError(message); setStep('view'); }, []);

  const takePhoto = () => {
    // A phone's own camera screen is better than anything drawn here.
    if (coarse() || !hasCamera()) selfieRef.current?.click();
    else { setError(null); setStep('camera'); }
  };

  const save = async () => {
    if (!source) return;
    setBusy(true);
    setError(null);
    try {
      const view = { cx: center.x, cy: center.y, side: Math.min(source.width, source.height) / zoom };
      const big = cropTo(source, view, OUT_FULL);
      const full = await encode(big);
      const thumb = await encode(downscale(big, OUT_THUMB), full.type);
      await savePhoto(full, thumb);
      setSource(null);
      setStep('view');
      setNotice('Your photo is saved. Colleagues will see it next time their screen updates.');
    } catch (e) {
      setError(e.message || 'Could not save your photo.');
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    setError(null);
    try {
      await removePhoto();
      setConfirmRemove(false);
      setNotice('Your photo is removed and deleted. Your initials show instead.');
    } catch (e) {
      setError(e.message || 'Could not remove your photo.');
    } finally {
      setBusy(false);
    }
  };

  const dropProps = mayUpload && step !== 'camera' ? {
    onDragOver: (e) => {
      if ([...(e.dataTransfer?.types || [])].includes('Files')) { e.preventDefault(); setDragging(true); }
    },
    onDragLeave: (e) => { if (e.currentTarget === e.target) setDragging(false); },
    onDrop: (e) => {
      e.preventDefault();
      setDragging(false);
      openFile(e.dataTransfer?.files?.[0]);
    }
  } : {};

  const footer = step === 'crop' ? (
    <div className="pe-foot">
      <button type="button" className="btn btn-secondary" disabled={busy}
              onClick={() => { setSource(null); setStep('view'); }}>Cancel</button>
      <button type="button" className="btn btn-primary pe-save" disabled={busy} onClick={save}>
        {busy ? 'Saving…' : 'Save photo'}
      </button>
    </div>
  ) : null;

  // Into the body: the account menu sits in the bar, and a fixed dialog
  // inside anything with a filter or transform is fixed to THAT instead.
  return createPortal((
    <Modal title="Your photo" onClose={busy ? undefined : onClose} footer={footer}>
      <div className="pe" data-dragging={dragging || undefined} {...dropProps}>
        {/* Hidden pickers: one for a file, one that asks a phone for its
            front camera. Both feed the same crop step. */}
        <input ref={fileRef} type="file" accept="image/*" className="pe-file" tabIndex={-1}
               aria-hidden="true" onChange={(e) => { openFile(e.target.files?.[0]); e.target.value = ''; }} />
        <input ref={selfieRef} type="file" accept="image/*" capture="user" className="pe-file" tabIndex={-1}
               aria-hidden="true" onChange={(e) => { openFile(e.target.files?.[0]); e.target.value = ''; }} />

        {!me && (
          <p className="pe-note">Profile photos are not available on this server yet.</p>
        )}

        {me && step === 'view' && (
          <div className="pe-view">
            <Avatar person={{ key: me.key, name }} className="pe-big" size={160} aria-hidden="true" />
            <div className="pe-view-text">
              <p className="pe-lead">
                {hasOwn
                  ? 'This is the photo in your circle, instead of your initials.'
                  : 'Add a photo of yourself and it replaces the initials in your circle.'}
              </p>
              <p className="pe-note">
                It shows in chat, calls, meetings and to-dos, to the people who can
                already see your name there. You can remove it at any time.
              </p>
              {hasOwn && me.shown === false && (
                <p className="pe-note pe-warn">
                  Profile photos are hidden in Settings at the moment, so nobody sees yours.
                  It is still saved; remove it if you would rather it was not.
                </p>
              )}
              {!mayUpload && me.reason && <p className="pe-note pe-warn">{me.reason}</p>}
              {mayUpload && (
                <div className="pe-row">
                  <button type="button" className="btn btn-primary" disabled={busy}
                          onClick={() => fileRef.current?.click()}>
                    {hasOwn ? 'Choose a new photo' : 'Choose a photo'}
                  </button>
                  {(hasCamera() || coarse()) && (
                    <button type="button" className="btn btn-secondary" disabled={busy} onClick={takePhoto}>
                      Take a photo
                    </button>
                  )}
                </div>
              )}
              {mayUpload && !coarse() && (
                <p className="pe-drop-hint" aria-hidden="true">…or drop a picture anywhere here.</p>
              )}
              {hasOwn && !confirmRemove && (
                <button type="button" className="pe-link pe-remove" disabled={busy}
                        onClick={() => setConfirmRemove(true)}>
                  Remove photo
                </button>
              )}
              {hasOwn && confirmRemove && (
                <div className="pe-confirm" role="group" aria-label="Remove your photo?">
                  <span>Remove your photo? It is deleted, not hidden.</span>
                  <button type="button" className="btn btn-danger btn-small" disabled={busy} onClick={remove}
                          autoFocus>
                    {busy ? 'Removing…' : 'Remove'}
                  </button>
                  <button type="button" className="btn btn-secondary btn-small" disabled={busy}
                          onClick={() => setConfirmRemove(false)}>
                    Keep it
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {me && step === 'camera' && (
          <Camera onTaken={begin} onCancel={() => setStep('view')} onError={cameraFailed} />
        )}

        {me && step === 'crop' && source && (
          <>
            <p className="pe-lead" id="pe-crop-title">Move and zoom until your face fills the circle.</p>
            <Cropper source={source} zoom={zoom} setZoom={setZoom} center={center} setCenter={setCenter}
                     labelId="pe-crop-title" />
            <p className="pe-note pe-privacy">
              <svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.6"
                   strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M8 1.8 3 3.8v3.6c0 3 2.1 5.6 5 6.8 2.9-1.2 5-3.8 5-6.8V3.8Z" />
                <path d="m5.8 8 1.6 1.6 2.9-3" />
              </svg>
              Saved as a small square picture. Camera details such as where it was
              taken (location data) are removed.
            </p>
            <button type="button" className="pe-link" disabled={busy} onClick={() => fileRef.current?.click()}>
              Choose a different picture
            </button>
          </>
        )}

        {opening && <p className="pe-note" role="status">Opening the picture…</p>}
        <p className="pe-status" role="status" aria-live="polite">{notice || ''}</p>
        {error && <p className="pe-error" role="alert">{error}</p>}
        {dragging && <div className="pe-drop" aria-hidden="true">Drop to use this picture</div>}
      </div>
    </Modal>
  ), document.body);
}
